<script context="module" lang="ts">
  enum ClientEditTab {
    ClientGroup,
    Users,
    Permissions,
    Notifications,
    Integrations,
    MYOBAdvancedIntegration,
    PCSchoolIntegration,
    SharepointV3Export,
    ClientNotes,
    FeatureFlags,
    CcrFeed,
    CcrStatement,
    Entities,
    EmailExport,
  }
</script>

<script lang="ts">
  import { onMount } from 'svelte';
  import { getDrawerStore, getModalStore, getToastStore, Tab, TabGroup } from '@skeletonlabs/skeleton';
  import { type ErrorResponse, type IdName, MenuConfig } from '$lib/models/types';
  import { getErrorMessage, isErrorResponse } from '$lib/utils/error/errorUtils';
  import { clientStore } from '$lib/stores/clientStore';
  import type {
    GetClientByIdResponse,
    PutBridgeClientNotificationRequest,
    PutClientRequest,
    CreateClientNoteRequest,
    GetClientNoteResponse,
    UpdateMenuConfig,
  } from '$lib/models/client/types';
  import { authStore } from '$lib/stores/authStore';
  import ClientDetails from '$lib/components/drawers/client/edit/tabs/ClientDetails.svelte';
  import Permissions from '$lib/components/drawers/client/edit/tabs/Permissions.svelte';
  import type { DataHandler } from '@vincjo/datatables/local';
  import { FlagBanner, Info, ListDashes, UsersThree, Database, Note, CreditCard } from 'phosphor-svelte';
  import Integrations from '$lib/components/drawers/client/edit/tabs/Integrations.svelte';
  import MYOBAdvancedIntegrationTab from '$lib/components/drawers/client/myobadvanced/MYOBAdvancedIntegrationTab.svelte';
  import PCSchoolIntegrationTab from '$lib/components/drawers/client/pcschool/PCSchoolIntegrationTab.svelte';
  import SharepointV3ExportTab from '$lib/components/drawers/client/sharepoint/SharepointV3ExportTab.svelte';
  import EmailExportTab from '$lib/components/drawers/client/email/EmailExportTab.svelte';
  import CcrFeedTab from '$lib/components/drawers/client/ccr/CcrFeedTab.svelte';
  import CcrStatementTab from '$lib/components/drawers/client/ccr/CcrStatementTab.svelte';
  import Notifications from '$lib/components/drawers/client/edit/tabs/Notifications.svelte';
  import IconButton from '$lib/components/common/iconButton/IconButton.svelte';
  import Button from '$lib/components/common/button/Button.svelte';
  import Users from '$lib/components/drawers/client/edit/tabs/Users.svelte';
  import { userStore, uiState, setUIState } from '$lib/stores/userStore';
  import { aliiConnectTypeStore } from '$lib/stores/aliiConnectTypeStore';
  import { aliiExportTypeStore } from '$lib/stores/aliiExportTypeStore';
  import ClientNotes from '$lib/components/drawers/client/note/ClientNotes.svelte';
  import FeatureFlags from '$lib/components/drawers/client/edit/tabs/FeatureFlags.svelte';
  import Entities from '$lib/components/drawers/client/edit/tabs/Entities.svelte';
  import { DrawerType } from '$lib/components/drawers/BaseDrawer.svelte';

  export let id: number;
  export let dataHandler: DataHandler;

  let isAdminDelete: boolean = false;

  const toastrStore = getToastStore();
  let loading: boolean = true;

  let tabSet: number = ClientEditTab.ClientGroup;

  let clientName: string | undefined;
  let clientStatus: number;
  let connectionTypeId: number;
  let exportTypeId: number;
  let aliiOCRType: number;
  let nanonetModelKey: string | undefined;
  let clientId: string | undefined;
  let clientKey: string | undefined;
  let location: string | undefined;
  let language: number | undefined;
  let domains: IdName[];
  let organisationId: string | undefined;
  let organisationName: string | undefined;
  let menuConfigs: UpdateMenuConfig[];
  let notificationConfigs: PutBridgeClientNotificationRequest[];
  let clientNotes: GetClientNoteResponse[];
  let userCount: number = 0;
  let entityId: string = '';

  let connectionTypes: IdName[] = [];
  $: connectionTypeName = connectionTypes.find((type) => type.id === connectionTypeId)?.name ?? '';
  $: isMYOBAdvancedConnectionType = connectionTypes.some((type) => type.id === connectionTypeId && type.name.toLowerCase() === 'myobadvanced');
  $: isPCSchoolConnectionType = connectionTypes.some((type) => type.id === connectionTypeId && type.name.toLowerCase() === 'pcschool');

  let exportTypes: IdName[] = [];
  $: isSharepointV3ExportType = exportTypes.some((type) => type.id === exportTypeId && type.name.toLowerCase() === 'sharepointv3');
  $: isEmailExportType = exportTypes.some((type) => type.id === exportTypeId && type.name.toLowerCase() === 'email');

  const drawerStore = getDrawerStore();
  const modalStore = getModalStore();

  let ccrFeedTab: any;

  async function refreshClientNotes(): Promise<void> {
    try {
      const result: GetClientByIdResponse | ErrorResponse = await clientStore.fetchById(id);
      if (isErrorResponse(result)) {
        toastrStore.trigger({
          message: getErrorMessage(result),
          background: 'bg-error-500',
        });
      } else {
        clientNotes = result.clientNotes;
      }
    } catch (err) {
      console.error('Error refreshing client notes:', err);
      toastrStore.trigger({
        message: 'Failed to refresh notes',
        background: 'bg-error-500',
      });
    }
  }

  onMount(() => {
    const unsubscribeIsAdminDelete = authStore.isAdminDelete.subscribe((value) => {
      isAdminDelete = value;
    });
    return unsubscribeIsAdminDelete;
  });

  onMount(async () => {
    try {
      // Get connection types to check if the client is using TASS
      connectionTypes = await aliiConnectTypeStore.get();
      exportTypes = await aliiExportTypeStore.get();
      await authStore.checkAdminDeleteStatus();

      const result: GetClientByIdResponse | ErrorResponse = await clientStore.fetchById(id);
      if (isErrorResponse(result)) {
        toastrStore.trigger({
          message: getErrorMessage(result),
          background: 'bg-error-500',
        });
      } else {
        clientName = result.clientName;
        clientStatus = result.clientStatus;
        connectionTypeId = result.connectionTypeId;
        exportTypeId = result.exportTypeId;
        aliiOCRType = result.aliiOCRType;
        nanonetModelKey = result.nanonetModelKey;
        clientKey = result.clientKey;
        location = result.location;
        language = result.language;
        clientId = String(result.id as number);
        domains = result.domains;
        menuConfigs = Object.values(MenuConfig).map((config) => ({
          name: config,
          enabled: result.menuConfigs.includes(config),
        }));
        notificationConfigs = result.notificationConfigs;
        clientNotes = result.clientNotes;
        organisationId = result.organisationId;
        organisationName = result.organisationName;
        userCount = await userStore.count(id);
      }
    } catch (err) {
    } finally {
      loading = false;
    }
  });

  function onCloseClick() {
    drawerStore.close();
  }

  async function onSave(): Promise<void> {
    const request: PutClientRequest = {
      id: id,
      clientName: clientName,
      clientStatus: clientStatus,
      connectionTypeId: connectionTypeId,
      exportTypeId: exportTypeId,
      aliiOCRType: aliiOCRType,
      nanonetModelKey: nanonetModelKey,
      location: location,
      language: language,
      domains: domains,
      menuConfigs: menuConfigs,
      notificationConfigs: notificationConfigs.filter((nc) => {
        if (nc.id !== undefined) return true;

        return nc.inApp || nc.email || !!nc.applyToAllUsers;
      }),
    };

    try {
      await clientStore.update(request);
      drawerStore.close();
      dataHandler.invalidate();
    } catch (errors) {
      if (Array.isArray(errors)) {
        for (let error of errors) {
          toastrStore.trigger({
            message: `${error.key} - ${error.value}`,
            background: 'bg-error-500',
            classes: 'text-white',
          });
        }
      } else {
        toastrStore.trigger({
          message: 'An unexpected error occurred',
          background: 'bg-error-500',
          classes: 'text-white',
        });
      }
    }
  }

  async function onCcrFeedSave(): Promise<void> {
    try {
      if (ccrFeedTab) {
        await ccrFeedTab.saveCCRSettings();
      }
    } catch (errors) {
      if (Array.isArray(errors)) {
        for (let error of errors) {
          toastrStore.trigger({
            message: `${error.key} - ${error.value}`,
            background: 'bg-error-500',
            classes: 'text-white',
          });
        }
      } else {
        toastrStore.trigger({
          message: 'An unexpected error occurred',
          background: 'bg-error-500',
          classes: 'text-white',
        });
      }
    }
  }

  function addUser() {
    setUIState('AddUserPage');
  }

  function openClientDeleteDrawer(id: number) {
    const drawerSettings: DrawerSettings = {
      id: DrawerType.ClientDelete,
      meta: { id: id, dataHandler: dataHandler },
      position: 'right',
    };

    drawerStore.open(drawerSettings);
  }

  function onDelete() {
    modalStore.trigger({
      type: 'confirm',
      title: 'Delete Client',
      body: `Are you sure you want to continue with the delete of the client "${clientName}"?<br><br>Upon confirmation, you will first be presented with a Delete Confirmation page with various deletion actions. The client will not be deleted immediately.`,
      response: (confirmed: boolean) => {
        if (confirmed) {
          modalStore.close();
          drawerStore.close();
          openClientDeleteDrawer(clientId);
        }
      },
    });
  }

  async function deleteClient(): Promise<void> {
    // TODO: implement client deletion once a delete API is available
  }

  $: if (!isMYOBAdvancedConnectionType && tabSet === ClientEditTab.MYOBAdvancedIntegration) {
    // If the user is on the MYOB Advanced Integration tab and the connection type changes from "myob" to something else,
    // move them to the Client Group tab
    tabSet = ClientEditTab.ClientGroup;
  }
  $: if (!isPCSchoolConnectionType && tabSet === ClientEditTab.PCSchoolIntegration) {
    // If the user is on the PCSchool Integration tab and the connection type changes from "pcschool" to something else,
    // move them to the Client Group tab
    tabSet = ClientEditTab.ClientGroup;
  }
  $: if (!isSharepointV3ExportType && tabSet === ClientEditTab.SharepointV3Export) {
    // If the user is on the Sharepoint V3 Export tab and the export type changes from "sharepointv3" to something else,
    // move them to the Client Group tab
    tabSet = ClientEditTab.ClientGroup;
  }
  $: if (!isEmailExportType && tabSet === ClientEditTab.EmailExport) {
    // If the user is on the Email Export tab and the export type changes from "email" to something else,
    // move them to the Client Group tab
    tabSet = ClientEditTab.ClientGroup;
  }
</script>

<div class="card flex h-full flex-col">
  <header class="flex justify-between border-b p-5">
    <div class="inline-flex items-center gap-2 p-4 text-2xl font-bold">
      <span>
        <UsersThree />
      </span>
      <span> Edit Client </span>
    </div>
    <div class="content-center">
      <IconButton icon="x" onClick={onCloseClick} />
    </div>
  </header>
  <div class="flex-grow overflow-y-auto p-4">
    {#if loading}
      <p>Loading...</p>
    {:else}
      <div class="p-5">
        <div class="mb-5 flex items-center justify-between text-xl font-bold text-primary-500">
          <span>{clientName}</span>
          {#if tabSet === 1 && userCount > 0 && $uiState === 'DataTable'}
            <div class="flex h-full flex-shrink-0 items-center">
              <Button
                active={true}
                label="Create User"
                size="medium"
                class={'!h-min py-[12px]'}
                icon="plus-square"
                iconSize="20px"
                colour="primary"
                fontSize="text-base"
                onClick={addUser}
              />
            </div>
          {/if}
        </div>

        <div>
          <TabGroup border="border-b">
            <Tab bind:group={tabSet} name="clientDetails" value={ClientEditTab.ClientGroup}>
              <div class="flex items-center gap-1">
                <span><ListDashes /></span> <span>Client Details</span>
              </div>
            </Tab>
            <Tab bind:group={tabSet} name="users" value={ClientEditTab.Users}>
              <div class="flex items-center gap-1">
                <span><UsersThree /></span> <span>Users</span>
              </div>
            </Tab>
            <Tab bind:group={tabSet} name="permissions" value={ClientEditTab.Permissions}>
              <div class="flex items-center gap-1">
                <span><FlagBanner /></span> <span>Menus</span>
              </div>
            </Tab>
            <Tab bind:group={tabSet} name="notifications" value={ClientEditTab.Notifications}>
              <div class="flex items-center gap-1">
                <span><Info /></span> <span>Notifications</span>
              </div>
            </Tab>
            <Tab bind:group={tabSet} name="ccrFeed" value={ClientEditTab.CcrFeed}>
              <div class="flex items-center gap-1">
                <span><CreditCard /></span> <span>CCR Feed</span>
              </div>
            </Tab>
            <Tab bind:group={tabSet} name="ccrStatements" value={ClientEditTab.CcrStatement}>
              <div class="flex items-center gap-1">
                <span><CreditCard /></span> <span>CCR Statement</span>
              </div>
            </Tab>
            <Tab bind:group={tabSet} name="clientNotes" value={ClientEditTab.ClientNotes}>
              <div class="flex items-center gap-1">
                <span>
                  <Note />
                </span>
                <span>Notes</span>
              </div>
            </Tab>
            <Tab bind:group={tabSet} name="entities" value={ClientEditTab.Entities}>
              <div class="flex items-center gap-1">
                <span><Database /></span> <span>Entities</span>
              </div>
            </Tab>
            <Tab bind:group={tabSet} name="featureflags" value={ClientEditTab.FeatureFlags}>
              <div class="flex items-center gap-1">
                <span><Database /></span> <span>Feature Flags</span>
              </div>
            </Tab>
            <Tab bind:group={tabSet} name="integrations" value={ClientEditTab.Integrations}>
              <div class="flex items-center gap-1">
                <span><Database /></span> <span>Integration</span>
              </div>
            </Tab>
            {#if isMYOBAdvancedConnectionType}
              <Tab bind:group={tabSet} name="myobadvancedIntegration" value={ClientEditTab.MYOBAdvancedIntegration}>
                <div class="flex items-center gap-1">
                  <span><Database /></span> <span>MYOB Advanced Integration</span>
                </div>
              </Tab>
            {/if}
            {#if isPCSchoolConnectionType}
              <Tab bind:group={tabSet} name="pcschoolIntegration" value={ClientEditTab.PCSchoolIntegration}>
                <div class="flex items-center gap-1">
                  <span><Database /></span> <span>PCSchool Integration</span>
                </div>
              </Tab>
            {/if}
            {#if isSharepointV3ExportType}
              <Tab bind:group={tabSet} name="sharepointV3Export" value={ClientEditTab.SharepointV3Export}>
                <div class="flex items-center gap-1">
                  <span><Database /></span> <span>Sharepoint Export</span>
                </div>
              </Tab>
            {/if}
            {#if isEmailExportType}
              <Tab bind:group={tabSet} name="emailExport" value={ClientEditTab.EmailExport}>
                <div class="flex items-center gap-1">
                  <span><Database /></span> <span>Email Export</span>
                </div>
              </Tab>
            {/if}
            <svelte:fragment slot="panel">
              {#if tabSet === ClientEditTab.ClientGroup}
                <ClientDetails
                  bind:clientName
                  bind:clientStatus
                  bind:connectionTypeId
                  bind:exportTypeId
                  bind:aliiOCRType
                  bind:nanonetModelKey
                  bind:clientId
                  bind:clientKey
                  bind:location
                  bind:language
                  bind:domains
                  {organisationId}
                  {organisationName}
                />
              {:else if tabSet === ClientEditTab.Users}
                <Users bind:clientId={id} />
              {:else if tabSet === ClientEditTab.Permissions}
                <Permissions bind:menuConfigs />
              {:else if tabSet === ClientEditTab.Notifications}
                <Notifications bind:notificationConfigs />
              {:else if tabSet === ClientEditTab.Integrations}
                <Integrations clientId={id} {clientName} {clientKey} {organisationId} connectionType={connectionTypeName} />
              {:else if tabSet === ClientEditTab.MYOBAdvancedIntegration && isMYOBAdvancedConnectionType}
                <MYOBAdvancedIntegrationTab clientId={id.toString()} {entityId} {clientName} />
              {:else if tabSet === ClientEditTab.PCSchoolIntegration && isPCSchoolConnectionType}
                <PCSchoolIntegrationTab clientId={id.toString()} {entityId} {clientName} />
              {:else if tabSet === ClientEditTab.SharepointV3Export && isSharepointV3ExportType}
                <SharepointV3ExportTab clientId={id.toString()} {clientName} />
              {:else if tabSet === ClientEditTab.EmailExport && isEmailExportType}
                <EmailExportTab clientId={id.toString()} {clientName} />
              {:else if tabSet === ClientEditTab.ClientNotes}
                <ClientNotes bind:clientNotes bind:clientId={id} onRefresh={refreshClientNotes} />
              {:else if tabSet === ClientEditTab.FeatureFlags}
                <FeatureFlags bind:clientId={id} />
              {:else if tabSet === ClientEditTab.CcrFeed}
                <CcrFeedTab bind:this={ccrFeedTab} clientId={id} />
              {:else if tabSet === ClientEditTab.CcrStatement}
                <CcrStatementTab clientId={id} {clientName} />
              {:else if tabSet === ClientEditTab.Entities}
                <Entities bind:clientId={id} />
              {/if}
            </svelte:fragment>
          </TabGroup>
        </div>
      </div>
    {/if}
  </div>
  <footer class="flex w-full justify-between gap-5 border-t p-4">
      {#if tabSet === ClientEditTab.ClientGroup || tabSet === ClientEditTab.Permissions || tabSet === ClientEditTab.Notifications}
        <Button label="Cancel" size="large" variant="outlined" onClick={onCloseClick} />
        <div class="inline-flex flex-wrap gap-5">
          {#if isAdminDelete && tabSet === ClientEditTab.ClientGroup}
            <Button label="Delete" colour="error" size="large" onClick={() => onDelete()} disabled={loading} />
          {/if}
          <Button label="Save Changes" colour="primary" size="large" onClick={() => onSave()} disabled={loading} />
        </div>
      {:else if tabSet === ClientEditTab.CcrFeed}
          <div></div>
          <div class="inline-flex flex-wrap gap-5">
          <Button label="Save Changes" colour="primary" size="large" onClick={() => onCcrFeedSave()} />
        </div>
      {/if}
  </footer>
  
</div>
